import { motion, useReducedMotion } from 'motion/react';

import { XStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';

type ProgressBarProps = {
  /** From 0 to 1. Leave it out for work whose progress is not known, and the bar moves without a value */
  value?: number;
  className?: string;
  fillClassName?: string;
  label?: string;
};

const FILL_CLASS = 'h-full rounded-full bg-foreground-subtle';

export const ProgressBar = ({
  value,
  className,
  fillClassName,
  label,
}: ProgressBarProps) => {
  const reduceMotion = useReducedMotion();
  const percent =
    value === undefined
      ? undefined
      : Math.round(Math.min(Math.max(value, 0), 1) * 100);

  return (
    <XStack
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent}
      aria-label={label}
      className={cn(
        'h-1.5 w-full overflow-hidden rounded-full bg-grayA-3',
        className
      )}
    >
      {percent === undefined ? (
        <motion.div
          className={cn(FILL_CLASS, 'w-2/5 shrink-0', fillClassName)}
          initial={{ x: '-100%' }}
          animate={reduceMotion ? { x: '75%' } : { x: ['-100%', '250%'] }}
          transition={
            reduceMotion
              ? { duration: 0 }
              : { duration: 1.6, ease: 'easeInOut', repeat: Infinity }
          }
        />
      ) : (
        <div
          className={cn(
            FILL_CLASS,
            'transition-[width] duration-300 ease-out',
            fillClassName
          )}
          style={{ width: `${percent}%` }}
        />
      )}
    </XStack>
  );
};
